import {cards, characters, isAlly, isBall, isDrill} from './cards';
import type {GameAction, PlayerObservation} from './types';

/** Pure decision policy: receives no opponent hand, Life Deck order, or RNG state. */
export function chooseAction(o:PlayerObservation,actions:GameAction[]):GameAction {
  if(!actions.length)throw new Error('The AI has no legal actions.');
  const me=o.players[o.playerId],foe=o.players[o.playerId===0?1:0];
  const controlled=o.combat?.controlling[o.playerId];
  const ally=me.board.find(c=>c.id===controlled);
  const stage=ally?.stage??me.stage;
  const ballCount=me.board.filter(c=>isBall(c.cardId)).length;
  const handAttack=(id:string)=>Boolean(cards[id]?.attack)&&!isAlly(id);
  const ownValue=(id:string)=>{
    const c=cards[id];if(!c)return 0;
    if(c.ball)return 8+(c.ball===7?3:0);
    if(isAlly(id))return 5;
    if(c.defense)return 5+(c.anger??0)*3+(c.id==='ss110'&&me.stage<5?3:0);
    if(c.attack)return 6+(c.anger??0)*3+(c.damage??0)*0.3-(c.cost??0)*0.2;
    if(isDrill(id))return 8;
    return isAlly(id)?5:4;
  };
  const score=(a:GameAction):number=>{
    const c=a.cardId?cards[a.cardId]:undefined;
    if(a.kind==='concede')return -10000;
    if(a.kind==='choose'){
      if(a.choice==='draw')return a.value?(me.deckCount<=a.value?-100:me.hand.length<5?70:30):25;
      if(a.choice==='capture'||a.choice==='ally-capture')return 100+(c?.ball===7?10:0)+(foe.board.filter(b=>isBall(b.cardId)).length===7?500:0);
      if(a.choice==='decline-capture')return -100;
      if(a.choice==='deal-damage')return foe.deckCount<4?180:10;
      if(a.choice==='recover')return ownValue(a.cardId!)*3;
      if(a.choice==='power-target')return a.target==='mp'?(10-me.stage)*10:Math.max(0,10-(me.board.find(b=>b.id===a.target)?.stage??0))*6;
      if(a.choice==='damage-target')return a.target==='mp'?me.stage*2-8:(me.board.find(b=>b.id===a.target)?.stage??0)*3;
      return 1;
    }
    if(a.kind==='discard'){
      const duplicateBall=c?.ball&&o.players.some(p=>p.board.some(b=>b.cardId===c.id));
      return duplicateBall?100:-ownValue(a.cardId!);
    }
    if(a.kind==='take-control'){
      if(o.phase==='defend'&&o.pending?.kind==='attack'&&o.pending.attack.attackType==='energy'&&o.pending.attack.life>2&&c?.effect==='energy-cap')return 85;
      return (me.board.find(b=>b.id===a.target)?.stage??0)>stage?65:-20;
    }
    if(a.kind==='use-power')return o.phase==='defend'?80:42-(c?.cost??0)*2;
    if(a.kind==='play-card'){
      if(o.phase==='defend')return 70+(c?.anger??0)*8+(me.level===2&&me.anger===4&&(c?.anger??0)>0?1000:0);
      if(o.phase==='noncombat'){
        if(c?.ball===5&&me.level===2&&me.anger>=3)return 1500;
        if(c?.ball===4&&me.level===2&&me.anger>=3)return 1500;
        return c?.ball?90+ballCount*3:isDrill(c?.id??'')?75:isAlly(c?.id??'')?72:65;
      }
      if(c?.ball===7)return ballCount===6?1500:me.hand.filter(h=>handAttack(h.cardId)).length>0?5:50;
      if(c?.effect==='capture')return 100+(ballCount===6?100:0);
      if(c?.attack)return 60+(c.anger??0)*9+(c.damage??0)*2-(c.cost??0)*2+(me.level===2&&me.anger===4&&(c.anger??0)>0?1000:0);
    }
    if(a.kind==='use-setup'){
      if(c?.effect==='ball6')return stage<=2&&me.hand.every(h=>!handAttack(h.cardId))?50:-15;
      if(c?.effect==='power-up')return stage<=5?80:me.discard.length?45:-10;
      return me.discard.length?55:-10;
    }
    if(a.kind==='start-combat'){
      const mp=cards[(me.mpIds??characters[me.character].personalityIds)[me.level-1]!];
      return me.hand.some(h=>handAttack(h.cardId))||Boolean(mp?.attack)?35:me.hand.length>=3?15:-5;
    }
    if(a.kind==='rest')return me.hand.some(h=>handAttack(h.cardId))?-5:10;
    if(a.kind==='final-physical')return 8-ownValue(a.cardId!)*0.5;
    if(a.kind==='pass')return 0;
    if(a.kind==='take-hit')return -5;
    return 1;
  };
  return [...actions].sort((a,b)=>score(b)-score(a)||a.id.localeCompare(b.id))[0]!;
}
